// ImportScreen — CSV-Import aus dem PVS (Dampsoft, Z1, charly …).
// Eine Dropzone für alles: dida erkennt am Spaltenkopf automatisch, ob es
// eine Patientenliste, Recall-Liste oder Umsatzstatistik ist — auch mehrere
// Dateien gleichzeitig.
import { useState, useEffect } from "react";
import { Card, CardHead, Btn, Select, Input, Insight, SH, Tbl, Td, Sp, MiniStat } from "./ui";
import { readFileSmart, parseCsv, guessMapping } from "./lib/csv";
import { parseIcs, mergeEventsIntoApts } from "./lib/ics";
import { listFeeds, addFeed, removeFeed, syncFeed } from "./lib/icsFeeds";
import { importPatients } from "./lib/patientStore";
import { importRecalls } from "./lib/recallStore";
import { upsertStats, listStats } from "./lib/statsStore";
import { DEFAULT_TYPES } from "./TermineScreen";
import { getBehandler } from "./lib/settingsStore";

const aptTypes = () => { try { const s = localStorage.getItem("dida_types"); if (s) return JSON.parse(s); } catch {} return DEFAULT_TYPES; };

const TYPES = {
  patienten:  { label: "Patientenliste",       color: "g" },
  recall:     { label: "Recall-Liste",         color: "b" },
  umsatz:     { label: "Umsatzstatistik",      color: "pu" },
  leistungen: { label: "Leistungsstatistik",   color: "b" },
  termine:    { label: "Terminkalender",       color: "a" },
};

// BEMA oder GOZ? Die GOZ nummeriert vierstellig (2040, 5170), der BEMA
// zwei- bis dreistellig mit Buchstabenzusätzen (13a, Ä1, P200). Das reicht
// als Erkennung — wer es genauer braucht, gibt eine Katalog-Spalte mit.
const katalogAusZiffer = (ziffer) => {
  const z = String(ziffer || "").trim().toUpperCase();
  if (/^\d{4}$/.test(z)) return "GOZ";
  if (z) return "BEMA";
  return "";
};
const normKatalog = (wert, ziffer) => {
  const w = String(wert || "").trim().toUpperCase();
  if (w.includes("GOZ")) return "GOZ";
  if (w.includes("BEMA") || w.includes("KASSE")) return "BEMA";
  return katalogAusZiffer(ziffer);
};

const FIELDS = {
  patienten: [
    { f: "vorname",  l: "Vorname",        req: true },
    { f: "nachname", l: "Nachname",       req: true },
    { f: "dob",      l: "Geburtsdatum" },
    { f: "kv",       l: "Krankenkasse" },
    { f: "email",    l: "E-Mail" },
    { f: "treat",    l: "Behandler" },
    { f: "last",     l: "Letzter Termin" },
    { f: "since",    l: "Patient seit" },
    { f: "status",   l: "Status" },
  ],
  recall: [
    { f: "vorname",  l: "Vorname",        req: true },
    { f: "nachname", l: "Nachname",       req: true },
    { f: "dob",      l: "Geburtsdatum" },
    { f: "lastPzr",  l: "Letzte PZR",     req: true },
    { f: "treat",    l: "Behandler" },
  ],
  umsatz: [
    { f: "monat",        l: "Monat / Periode", req: true },
    { f: "umsatz",       l: "Umsatz (€)",      req: true },
    { f: "behandler",    l: "Behandler" },
    { f: "neupatienten", l: "Neupatienten" },
    { f: "privatanteil", l: "Privatanteil" },
  ],
  leistungen: [
    { f: "monat",     l: "Monat / Periode",  req: true },
    { f: "ziffer",    l: "Ziffer (BEMA/GOZ)", req: true },
    { f: "anzahl",    l: "Anzahl",           req: true },
    { f: "betrag",    l: "Honorar (€)" },
    { f: "katalog",   l: "Katalog (BEMA/GOZ)" },
    { f: "bezeichnung", l: "Bezeichnung" },
    { f: "behandler", l: "Behandler" },
  ],
};

// Welcher Listentyp passt zu diesen Spalten? Pro Typ raten wir das Mapping
// und bewerten: Pflichtfelder komplett = qualifiziert, mehr Treffer = besser.
function detectType(headers) {
  let best = null;
  Object.keys(FIELDS).forEach(typ => {
    const mapping = guessMapping(headers, FIELDS[typ].map(x => x.f));
    const matched = Object.keys(mapping).length;
    const reqOk = FIELDS[typ].filter(x => x.req).every(x => mapping[x.f] !== undefined);
    const score = matched + (reqOk ? 10 : 0);
    if (!best || score > best.score) best = { typ, mapping, score, reqOk };
  });
  return best;
}

const parseNum = (s) => {
  if (s === undefined || s === null) return null;
  const clean = String(s).replace(/[€\s]/g, "").replace(/\./g, "").replace(",", ".");
  const n = parseFloat(clean);
  return isNaN(n) ? null : n;
};

const loadJson = (key, fallback) => { try { const s = localStorage.getItem(key); if (s) return JSON.parse(s); } catch {} return fallback; };
const saveJson = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };

const mapRows = (file) => file.rows.map(r => {
  const obj = {};
  FIELDS[file.typ].forEach(({ f }) => {
    const idx = file.mapping[f];
    obj[f] = idx !== undefined && idx !== "" ? (r[idx] ?? "") : "";
  });
  return obj;
});

async function importFile(file) {
  if (file.kind === "ics") {
    return mergeEventsIntoApts(file.events, { behandler: file.behandler, type: file.vtype });
  }
  const rows = mapRows(file);
  if (file.typ === "patienten") {
    const r = await importPatients(rows);
    return { added: r.added, updated: r.updated };
  }
  if (file.typ === "recall") {
    return importRecalls(rows.filter(r => r.vorname && r.nachname).map(r => ({
      name: `${r.nachname.trim()}, ${r.vorname.trim()}`,
      dob: r.dob || "", lastPzr: r.lastPzr || "", behandler: r.treat || "",
    })));
  }
  // Leistungsstatistik: eine Zeile je Ziffer und Periode. Der Schlüssel führt
  // Katalog, Ziffer und Behandler mit — sonst würde eine Behandler-Datei die
  // Praxiszahlen derselben Ziffer überschreiben.
  if (file.typ === "leistungen") {
    const assign = (file.assignTo || "").trim();
    const rowsOut = [];
    rows.filter(r => r.monat && r.ziffer && r.anzahl !== "").forEach(r => {
      const monat = r.monat.trim();
      const ziffer = r.ziffer.trim();
      const katalog = normKatalog(r.katalog, ziffer);
      const beh = (r.behandler || "").trim() || assign;
      const anzahl = parseNum(r.anzahl);
      if (!anzahl) return;
      rowsOut.push({
        typ: "leistungen",
        periode: `${monat}|${katalog}|${ziffer}${beh ? `|${beh}` : ""}`,
        quelle: file.name,
        payload: {
          monat, ziffer, katalog, anzahl,
          ...(r.betrag !== "" ? { betrag: parseNum(r.betrag) } : {}),
          ...(r.bezeichnung ? { bezeichnung: r.bezeichnung.trim() } : {}),
          ...(beh ? { behandler: beh } : {}),
        },
      });
    });
    if (!rowsOut.length) return { added: 0, updated: 0, error: "Keine auswertbaren Zeilen — Ziffer und Anzahl müssen gefüllt sein." };
    const r = await upsertStats(rowsOut);
    return { added: 0, updated: r.updated, ziffern: new Set(rowsOut.map(x => x.payload.ziffer)).size };
  }

  // Umsatzstatistik — mit oder ohne Behandler-Dimension:
  //  · Behandler-Spalte in der CSV  → Zeilen je Behandler + Praxis-Summe je Monat
  //  · "Datei gehört zu"-Zuordnung  → Zeilen je Behandler, Praxis-Stats unangetastet
  //    (eine Datei pro Behandler ist nur ein Ausschnitt — würde sonst die
  //    Praxis-Summe des Monats mit Teilwerten überschreiben)
  //  · weder noch                   → wie bisher Praxis-Ebene
  const hasBehCol = file.mapping.behandler !== undefined && file.mapping.behandler !== "";
  const assignTo = (file.assignTo || "").trim();
  const statRows = [], behRows = [], praxisAgg = new Map();
  rows.filter(r => r.monat && r.umsatz !== "").forEach(r => {
    const monat = r.monat.trim();
    const beh = hasBehCol ? (r.behandler || "").trim() : assignTo;
    const payload = {
      umsatz: parseNum(r.umsatz),
      ...(r.neupatienten !== "" ? { neupatienten: parseNum(r.neupatienten) } : {}),
      ...(r.privatanteil !== "" ? { privatanteil: parseNum(r.privatanteil) } : {}),
    };
    if (beh) behRows.push({ typ: "umsatz_behandler", periode: `${monat}|${beh}`, quelle: file.name, payload: { ...payload, monat, behandler: beh } });
    else statRows.push({ typ: "umsatz", periode: monat, quelle: file.name, payload });
    if (hasBehCol) {
      const agg = praxisAgg.get(monat) || { umsatz: 0, neupatienten: 0 };
      agg.umsatz += payload.umsatz || 0;
      agg.neupatienten += payload.neupatienten || 0;
      praxisAgg.set(monat, agg);
    }
  });
  // Spalten-Variante: die Datei deckt alle Behandler ab → Summe = Praxis-Monat
  if (hasBehCol) praxisAgg.forEach((agg, monat) => statRows.push({
    typ: "umsatz", periode: monat, quelle: file.name,
    payload: { umsatz: agg.umsatz, ...(agg.neupatienten ? { neupatienten: agg.neupatienten } : {}) },
  }));
  let updated = 0;
  if (statRows.length) updated += (await upsertStats(statRows)).updated;
  if (behRows.length) updated += (await upsertStats(behRows)).updated;
  return { added: 0, updated, beh: behRows.length };
}

export default function ImportScreen() {
  const [files, setFiles] = useState([]);   // {id,name,headers,rows,typ,mapping,result}
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(() => loadJson("dida_import_log", []));
  const [stats, setStats] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [feeds, setFeeds] = useState(listFeeds);
  const BEHANDLER = getBehandler();
  const [feedForm, setFeedForm] = useState({ url: "", label: "", behandler: BEHANDLER[0]?.id || "", type: aptTypes()[0]?.id || "kontrolle" });
  const [feedBusy, setFeedBusy] = useState(null);
  const [feedMsg, setFeedMsg] = useState({});

  const addFeedClick = () => {
    if (!getBehandler().length) { setFeedMsg(m => ({ ...m, form: { error: "Zuerst Behandler anlegen (Benutzermenü → Praxis & Behandler)." } })); return; }
    if (!feedForm.url.trim().startsWith("http")) { setFeedMsg(m => ({ ...m, form: { error: "Bitte eine vollständige https-URL einfügen." } })); return; }
    setFeeds(addFeed(feedForm));
    setFeedForm(f => ({ ...f, url: "", label: "" }));
    setFeedMsg(m => ({ ...m, form: null }));
  };
  const syncOne = async (f) => {
    setFeedBusy(f.id);
    try { const r = await syncFeed(f); setFeedMsg(m => ({ ...m, [f.id]: r })); }
    catch (e) { setFeedMsg(m => ({ ...m, [f.id]: { error: e?.message || String(e) } })); }
    setFeeds(listFeeds());
    setFeedBusy(null);
  };

  useEffect(() => { listStats("umsatz").then(setStats).catch(() => {}); }, [busy]);

  const addFiles = async (fileList) => {
    for (const f of Array.from(fileList || [])) {
      try {
        const text = await readFileSmart(f);
        // iCalendar? (Outlook/Google/Apple-Export) → Termin-Import
        if (f.name.toLowerCase().endsWith(".ics") || text.includes("BEGIN:VCALENDAR")) {
          const parsed = parseIcs(text);
          setFiles(prev => [...prev, { id: Date.now() + Math.random(), kind: "ics", name: f.name,
            events: parsed?.events || [], skipped: parsed?.skipped || {}, behandler: getBehandler()[0]?.id || "", vtype: aptTypes()[0]?.id || "kontrolle",
            typ: "termine", sure: true, result: parsed && parsed.events.length ? null : { error: "Keine importierbaren Termine gefunden." } }]);
          continue;
        }
        const { headers, rows } = parseCsv(text);
        if (!headers.length) continue;
        // gespeichertes Mapping wiederverwenden, wenn Kopfzeile bekannt
        const det = detectType(headers);
        const saved = loadJson("dida_import_mappings", {})[det.typ];
        const mapping = (saved && saved.headers === headers.join("|")) ? saved.mapping : det.mapping;
        setFiles(prev => [...prev, { id: Date.now() + Math.random(), name: f.name, headers, rows, typ: det.typ, mapping, sure: det.reqOk, result: null }]);
      } catch (e) {
        setFiles(prev => [...prev, { id: Date.now() + Math.random(), name: f.name, headers: [], rows: [], typ: "patienten", mapping: {}, sure: false, result: { error: "Datei nicht lesbar: " + (e?.message || e) } }]);
      }
    }
  };

  const update = (id, patch) => setFiles(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
  const remove = (id) => setFiles(prev => prev.filter(f => f.id !== id));

  const missingReq = (f) => f.kind === "ics" ? (getBehandler().length ? [] : ["Behandler"]) : FIELDS[f.typ].filter(x => x.req && (f.mapping[x.f] === undefined || f.mapping[x.f] === "")).map(x => x.l);
  const ready = files.filter(f => !f.result && missingReq(f).length === 0);

  const runAll = async () => {
    setBusy(true);
    const newLog = [...log];
    for (const f of files) {
      if (f.result || missingReq(f).length) continue;
      try {
        const res = await importFile(f);
        update(f.id, { result: res });
        const mappings = loadJson("dida_import_mappings", {});
        mappings[f.typ] = { headers: f.headers.join("|"), mapping: f.mapping };
        saveJson("dida_import_mappings", mappings);
        newLog.unshift({ ts: new Date().toLocaleString("de-DE"), typ: f.typ, file: f.name, added: res.added, updated: res.updated });
      } catch (e) {
        update(f.id, { result: { error: e?.message || String(e) } });
      }
    }
    const trimmed = newLog.slice(0, 12);
    setLog(trimmed); saveJson("dida_import_log", trimmed);
    setBusy(false);
  };

  // Daten-Frische: wann kam zuletzt ein Import je Typ? (aus der Historie)
  const freshness = Object.keys(TYPES).map(typ => {
    const hit = log.find(e => e.typ === typ || e.typ === TYPES[typ].label);
    if (!hit) return { typ, txt: "noch nie", stale: true };
    const then = new Date(hit.ts.replace(/(\d+)\.(\d+)\.(\d+),/, "$3-$2-$1"));
    const days = isNaN(then) ? null : Math.floor((Date.now() - then.getTime()) / 86400000);
    return { typ, txt: days === null ? hit.ts : days === 0 ? "heute" : `vor ${days} Tag${days === 1 ? "" : "en"}`, stale: days === null ? false : days > 7 };
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Daten-Frische */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 7 }}>
        {freshness.map(f => (
          <div key={f.typ} style={{ background: "var(--mf-bg)", border: "0.5px solid var(--mf-border)", borderRadius: 10, padding: "10px 12px" }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: f.stale ? "var(--mf-neg)" : "var(--mf-text)" }}>{f.txt}</div>
            <div style={{ fontSize: 10, color: "var(--mf-muted)", marginTop: 1 }}>Letzter Import: {TYPES[f.typ].label}</div>
            {f.stale && <div style={{ fontSize: 10, marginTop: 3, color: "var(--mf-neg)" }}>Aktualisierung empfohlen</div>}
          </div>
        ))}
      </div>

      {/* Eine Dropzone für alles */}
      <Card>
        <CardHead title="CSV-Dateien importieren" icon="ti-file-import" />
        <label
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "24px 14px", borderRadius: 13, cursor: "pointer",
            border: `1.5px dashed ${dragOver ? "var(--mf-primary)" : "var(--mf-border2)"}`, background: dragOver ? "rgba(53,201,155,.06)" : "var(--mf-bg-alt)", transition: "all .15s" }}>
          <i className="ti ti-upload" style={{ fontSize: 22, color: "var(--mf-primary)" }} />
          <span style={{ fontSize: 12, color: "var(--mf-text)", fontWeight: 500 }}>Eine oder mehrere CSV-Dateien hierher ziehen</span>
          <span style={{ fontSize: 10, color: "var(--mf-muted)" }}>dida erkennt automatisch, ob es Patienten, Recalls oder Umsätze sind</span>
          <input type="file" accept=".csv,.txt" multiple style={{ display: "none" }} onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
        </label>
      </Card>

      {/* Erkannte Dateien */}
      {files.map(f => {
        if (f.kind === "ics") return (
          <Card key={f.id}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              <i className="ti ti-calendar-down" style={{ fontSize: 15, color: "var(--mf-primary)" }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--mf-text)" }}>{f.name}</span>
              <span style={{ fontSize: 10, color: "var(--mf-muted)" }}>{f.events.length} Termine{(f.skipped?.rrule || f.skipped?.allday) ? ` · übersprungen: ${f.skipped.rrule || 0} Serientermine, ${f.skipped.allday || 0} ganztägig` : ""}</span>
              <Sp color="a">Terminkalender</Sp>
              <div style={{ flex: 1 }} />
              <Btn small onClick={() => remove(f.id)}>Entfernen</Btn>
            </div>
            {f.result ? (
              f.result.error
                ? <Insight tone="r"><strong>Fehler:</strong> {f.result.error}</Insight>
                : <Insight tone="g"><strong>Importiert:</strong> {f.result.added} neue Termine, {f.result.updated} aktualisiert → Terminplan</Insight>
            ) : !getBehandler().length ? (
              <Insight tone="a"><strong>Zuerst Behandler anlegen</strong> (Benutzermenü unten links → Praxis &amp; Behandler) — Termine brauchen eine Behandler-Zuordnung.</Insight>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                  <Select label="Behandler zuordnen" value={f.behandler} onChange={v => update(f.id, { behandler: v })}
                    options={BEHANDLER.map(b => ({ value: b.id, label: b.name }))} />
                  <Select label="Termintyp" value={f.vtype} onChange={v => update(f.id, { vtype: v })}
                    options={aptTypes().map(t => ({ value: t.id, label: t.label }))} />
                </div>
                <SH t="Vorschau" />
                <Tbl heads={["Datum", "Zeit", "Dauer", "Titel"]}>
                  {f.events.slice(0, 4).map((ev, i) => (
                    <tr key={i}><Td>{ev.date}</Td><Td>{ev.time}</Td><Td>{ev.dur} Min.</Td><Td bold>{ev.summary}</Td></tr>
                  ))}
                </Tbl>
              </>
            )}
          </Card>
        );
        const miss = missingReq(f);
        const rows = f.headers.length ? mapRows(f) : [];
        const shown = FIELDS[f.typ].filter(({ f: k }) => f.mapping[k] !== undefined && f.mapping[k] !== "");
        return (
          <Card key={f.id}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              <i className="ti ti-file-spreadsheet" style={{ fontSize: 15, color: "var(--mf-primary)" }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--mf-text)" }}>{f.name}</span>
              <span style={{ fontSize: 10, color: "var(--mf-muted)" }}>{f.rows.length} Zeilen</span>
              <Sp color={TYPES[f.typ].color}>{TYPES[f.typ].label}</Sp>
              {!f.sure && !f.result && <Sp color="a">Typ bitte prüfen</Sp>}
              <div style={{ flex: 1 }} />
              <Select value={f.typ} onChange={typ => update(f.id, { typ, mapping: guessMapping(f.headers, FIELDS[typ].map(x => x.f)), result: null })}
                options={Object.entries(TYPES).map(([value, t]) => ({ value, label: t.label }))} style={{ marginBottom: 0, width: 170 }} />
              <Btn small onClick={() => remove(f.id)}>Entfernen</Btn>
            </div>

            {f.result ? (
              f.result.error
                ? <Insight tone="r"><strong>Fehler:</strong> {f.result.error}</Insight>
                : <Insight tone="g"><strong>Importiert:</strong> {f.result.added} neu, {f.result.updated} aktualisiert.{f.typ === "recall" && " → Tracking → Recall"}{f.typ === "patienten" && " → Patientenliste"}{f.typ === "umsatz" && f.result.beh > 0 && ` Davon ${f.result.beh} Behandler-Zeilen → Umsatz & Kosten → „Umsatz je Behandler“.`}{f.typ === "leistungen" && ` ${f.result.ziffern} verschiedene Ziffern → Tracking → „Leistungsspektrum“.`}</Insight>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: "0 12px" }}>
                  {FIELDS[f.typ].map(({ f: k, l, req }) => (
                    <Select key={k} label={`${l}${req ? " *" : ""}`}
                      value={f.mapping[k] !== undefined ? String(f.mapping[k]) : ""}
                      onChange={v => update(f.id, { mapping: { ...f.mapping, [k]: v === "" ? "" : parseInt(v) } })}
                      options={[{ value: "", label: "— nicht importieren —" }, ...f.headers.map((h, i) => ({ value: String(i), label: h || `Spalte ${i + 1}` }))]} />
                  ))}
                </div>
                {(f.typ === "umsatz" || f.typ === "leistungen") && (f.mapping.behandler === undefined || f.mapping.behandler === "") && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }}>
                    <Select label="Diese Datei gehört zu" value={f.assignTo || ""} onChange={v => update(f.id, { assignTo: v })}
                      options={[{ value: "", label: "Gesamte Praxis (keine Behandler-Zuordnung)" }, ...getBehandler().map(b => ({ value: b.name, label: b.name }))]}
                      style={{ marginBottom: 0, maxWidth: 360 }} />
                    <div style={{ fontSize: 10, color: "var(--mf-muted)" }}>Für PVS-Exporte „ein Export pro Behandler“ (z.B. DS-Win Umsatzstatistik) — die Zahlen erscheinen dann dem Behandler zugeordnet.</div>
                  </div>
                )}
                {f.typ === "umsatz" && f.mapping.behandler !== undefined && f.mapping.behandler !== "" && (
                  <Insight tone="g">Behandler-Spalte erkannt — Umsätze werden je Behandler zugeordnet, die Monatssummen ergeben die Praxis-Werte.</Insight>
                )}
                {miss.length > 0 && <Insight tone="a">Pflichtfelder fehlen: <strong>{miss.join(", ")}</strong></Insight>}
                {shown.length > 0 && rows.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <SH t="Vorschau" />
                    <Tbl heads={shown.map(x => x.l)}>
                      {rows.slice(0, 3).map((r, i) => (
                        <tr key={i}>{shown.map(({ f: k }) => <Td key={k}>{r[k] || <span style={{ color: "var(--mf-border2)" }}>—</span>}</Td>)}</tr>
                      ))}
                    </Tbl>
                  </div>
                )}
              </>
            )}
          </Card>
        );
      })}

      {ready.length > 0 && (
        <Btn color="g" lg onClick={runAll}>{busy ? "Importiere…" : `${ready.length} Datei${ready.length === 1 ? "" : "en"} importieren`}</Btn>
      )}

      {/* Kalender-Abos (Parallelbetrieb) */}
      <Card>
        <CardHead title="Kalender-Abo — Termine automatisch übernehmen" icon="ti-refresh" />
        {feeds.length === 0 && (
          <div style={{ fontSize: 11, color: "var(--mf-muted)", marginBottom: 10, lineHeight: 1.5 }}>
            Für den Parallelbetrieb: Hinterlege die geheime ICS-URL deines bestehenden Kalenders — dida holt die Termine bei jedem App-Start automatisch.
            <br/>Outlook: Kalender-Einstellungen → „Kalender veröffentlichen" → ICS-Link · Google: Einstellungen → „Privatadresse im iCal-Format".
          </div>
        )}
        {feeds.map(f => {
          const msg = feedMsg[f.id] || f.lastResult;
          return (
            <div key={f.id} style={{ padding: "8px 0", borderBottom: "0.5px solid var(--mf-border)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <i className="ti ti-calendar-repeat" style={{ fontSize: 14, color: "var(--mf-primary)" }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--mf-text)" }}>{f.label}</span>
                <span style={{ fontSize: 10, color: "var(--mf-muted)" }}>→ {BEHANDLER.find(b => b.id === f.behandler)?.name || f.behandler}</span>
                {f.lastSync && <span style={{ fontSize: 10, color: "var(--mf-muted)" }}>zuletzt: {f.lastSync}</span>}
                <div style={{ flex: 1 }} />
                <Btn small color="g" onClick={() => syncOne(f)}>{feedBusy === f.id ? "Synce…" : "Jetzt syncen"}</Btn>
                <Btn small onClick={() => setFeeds(removeFeed(f.id))}>Entfernen</Btn>
              </div>
              {msg && (msg.error
                ? <div style={{ fontSize: 10, color: "var(--mf-neg)", marginTop: 4 }}>{msg.error}</div>
                : <div style={{ fontSize: 10, color: "var(--mf-primary)", marginTop: 4 }}>✓ {msg.events} Termine gelesen · {msg.added} neu, {msg.updated} aktualisiert{msg.skipped?.rrule ? ` · ${msg.skipped.rrule} Serientermine übersprungen` : ""}</div>
              )}
            </div>
          );
        })}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "0 12px", marginTop: 12 }}>
          <Input label="ICS-Kalender-URL" value={feedForm.url} onChange={v => setFeedForm(s => ({ ...s, url: v }))} placeholder="https://…/calendar.ics"/>
          <Input label="Bezeichnung (optional)" value={feedForm.label} onChange={v => setFeedForm(s => ({ ...s, label: v }))} placeholder="z.B. Outlook Dr. Hoffmann"/>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "0 12px", alignItems: "start" }}>
          <Select label="Behandler" value={feedForm.behandler} onChange={v => setFeedForm(s => ({ ...s, behandler: v }))} options={BEHANDLER.map(b => ({ value: b.id, label: b.name }))}/>
          <Select label="Termintyp" value={feedForm.type} onChange={v => setFeedForm(s => ({ ...s, type: v }))} options={aptTypes().map(t => ({ value: t.id, label: t.label }))}/>
          <Btn color="g" lg onClick={addFeedClick}>Abo hinzufügen</Btn>
        </div>
        {feedMsg.form?.error && <div style={{ fontSize: 10, color: "var(--mf-neg)" }}>{feedMsg.form.error}</div>}
      </Card>

      {/* Importierte Umsatzstatistiken */}
      {stats.length > 0 && (
        <Card>
          <CardHead title="Importierte Umsatzstatistik" icon="ti-chart-bar" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 7, marginBottom: 10 }}>
            <MiniStat v={String(stats.length)} l="Perioden erfasst" />
            <MiniStat v={`€ ${Math.round(stats.reduce((s, r) => s + (r.payload?.umsatz || 0), 0)).toLocaleString("de-DE")}`} l="Summe importierter Umsatz" tone="g" />
            <MiniStat v={stats[stats.length - 1]?.periode || "—"} l="Letzte Periode" />
          </div>
          <Tbl heads={["Periode", "Umsatz", "Neupatienten", "Privatanteil", "Quelle"]}>
            {stats.map(r => (
              <tr key={r.typ + r.periode}>
                <Td bold>{r.periode}</Td>
                <Td>{r.payload?.umsatz != null ? `€ ${Number(r.payload.umsatz).toLocaleString("de-DE")}` : "—"}</Td>
                <Td>{r.payload?.neupatienten ?? "—"}</Td>
                <Td>{r.payload?.privatanteil != null ? `${r.payload.privatanteil}` : "—"}</Td>
                <Td color="var(--mf-muted)">{r.quelle || "—"}</Td>
              </tr>
            ))}
          </Tbl>
        </Card>
      )}

      {/* Historie */}
      {log.length > 0 && (
        <Card>
          <CardHead title="Import-Historie" icon="ti-history" />
          <Tbl heads={["Zeitpunkt", "Typ", "Datei", "Ergebnis"]}>
            {log.map((e, i) => (
              <tr key={i}>
                <Td color="var(--mf-muted)">{e.ts}</Td>
                <Td><Sp color={TYPES[e.typ]?.color || "b"}>{TYPES[e.typ]?.label || e.typ}</Sp></Td>
                <Td>{e.file}</Td>
                <Td>{e.added} neu · {e.updated} aktualisiert</Td>
              </tr>
            ))}
          </Tbl>
        </Card>
      )}

      <Insight>
        <strong>Empfohlener Rhythmus:</strong> Patientenliste + Recall-Liste <strong>wöchentlich</strong> (z.B. Montagmorgen — Recall-Intervalle bewegen sich in Monaten, täglicher Export bringt nichts), Umsatzstatistik <strong>wöchentlich oder monatlich</strong> — dank Aktualisierung pro Periode kannst du denselben Monat beliebig oft neu hochladen. Die Frische-Anzeige oben erinnert dich, wenn Daten älter als 7 Tage sind.
      </Insight>
    </div>
  );
}
